import { useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import DebugOverlay from './components/DebugOverlay';
import { useHideMaintainerTools, useZh } from './lib/useProduct';

// Tiny redirect shim so old `/apply/:id` links still land in the right place.
function ApplyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/apps/${encodeURIComponent(id || '')}?tab=apply`} replace />;
}

// Route guard for the English maintainer-only tools (Scanner, Settings with the
// BYOK key card, Interview/Offers/Outreach). In the UK-CN student product these
// are hidden from the nav; this stops a non-admin student reaching them by
// typing the URL. Admins keep access (the product flag is deployment-wide).
function MaintainerRoute({ children }: { children: React.ReactElement }) {
  if (useHideMaintainerTools()) return <Navigate to="/profile" replace />;
  return children;
}
import Shell from './components/Shell';
import Landing from './routes/Landing';
import { Privacy, Terms } from './routes/Legal';
import Matches from './routes/Matches';
import Board from './routes/Board';
import BoardJob from './routes/BoardJob';
import NarrativeCV from './routes/NarrativeCV';
import Scanner from './routes/Scanner';
import JobDetail from './routes/JobDetail';
import Apps from './routes/Apps';
import ApplicationDetail from './routes/ApplicationDetail';
import Interview from './routes/Interview';
import Offers from './routes/Offers';
import OffersCompare from './routes/OffersCompare';
import Outreach from './routes/Outreach';
import OutreachDetail from './routes/OutreachDetail';
import Compose from './routes/Compose';
import Profile from './routes/Profile';
import Settings from './routes/Settings';
import SettingsFilter from './routes/SettingsFilter';
import CVPicker from './routes/CVPicker';
import MyCV from './routes/MyCV';
import CVEditor from './routes/CVEditor';
import UploadCV from './routes/UploadCV';
import Practice from './routes/Practice';
import PrepPack from './routes/PrepPack';
import InterviewSession from './routes/InterviewSession';
// Stories (global story bank) was removed in Phase 14 cleanup. Per-position
// prep packs include a Story Bank Mapping table that's better-tailored, so the
// global bank was redundant. The `interview-prep/story-bank.md` disk file is
// untouched — users can still maintain it manually and it's read by the
// interview_prep / generate_answer prompts.
import Recap from './routes/Recap';
import Negotiate from './routes/Negotiate';
import Templates from './routes/Templates';
import AddApp from './routes/AddApp';
import Register from './routes/Register';
import Login from './routes/Login';
import ForgotPassword from './routes/ForgotPassword';
import ResetPassword from './routes/ResetPassword';
import VerifyEmail from './routes/VerifyEmail';
import AdminInvites from './routes/AdminInvites';
import AdminUsers from './routes/AdminUsers';

export default function App() {
  // index.html hardcodes lang="en", so the Chinese product served itself as an
  // English document: screen readers pick an English voice for 中文 content and
  // Chrome offers to translate a page that is already in the reader's language.
  // It also gives the stylesheet something to hang script-specific typography
  // on — Latin eyebrows want uppercase + wide tracking, CJK wants neither.
  const zh = useZh();
  useEffect(() => {
    document.documentElement.lang = zh ? 'zh-CN' : 'en';
  }, [zh]);

  return (
    <>
    <Routes>
      {/* Outside the sidebar shell — no DebugOverlay; nothing to debug here
          and the floating button overlapped the landing's "Get started" CTA. */}
      <Route path="/" element={<Landing />} />
      {/* /onboarding (the 4-step wizard) was removed — /profile is the single
          first-run flow: Register lands there and Shell redirects anyone with
          onboarded=false there. Old bookmarks fall through to the catch-all. */}

      {/* Auth flow — public pages (server gate lets these through unauth). */}
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* Legal — must be reachable WITHOUT an account: people are entitled to
          read what happens to their data before handing any of it over. */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      {/* Admin (gated client-side; server enforces is_admin too). */}
      <Route path="/admin/invites" element={<AdminInvites />} />
      <Route path="/admin/users" element={<AdminUsers />} />

      {/* Inside the shell — DebugOverlay rendered here so it only shows up
          on workspace routes that actually run jobs. */}
      <Route element={<><DebugOverlay /><Shell /></>}>
        <Route path="/matches" element={<Matches />} />
        {/* Shared, cross-tenant board — see routes/Board.tsx. */}
        <Route path="/board" element={<Board />} />
        {/* Public detail page for a board row. NOT /job/:id — that route
            below is the signed-in evaluation detail, a different resource. */}
        <Route path="/jobs/:id" element={<BoardJob />} />
        <Route path="/scanner" element={<MaintainerRoute><Scanner /></MaintainerRoute>} />
        <Route path="/job/:id" element={<JobDetail />} />

        <Route path="/apps" element={<Apps />} />
        <Route path="/apps/:id" element={<ApplicationDetail />} />
        <Route path="/apps/add" element={<AddApp />} />
        {/* Legacy /apply/:id merged into /apps/:id?tab=apply — redirect old bookmarks */}
        <Route path="/apply/:id" element={<ApplyRedirect />} />

        <Route path="/interview" element={<MaintainerRoute><Interview /></MaintainerRoute>} />
        <Route path="/interview/:sessionId" element={<MaintainerRoute><InterviewSession /></MaintainerRoute>} />
        {/* Legacy: old standalone PrepPack route — kept available but list page
            no longer links here. Bookmarks still work; we don't redirect since
            it's a slightly different UX than the new tab. */}
        <Route path="/interview/:sessionId/prep" element={<MaintainerRoute><PrepPack /></MaintainerRoute>} />
        {/* Old practice URL → new tab */}
        <Route path="/interview/:sessionId/practice" element={<MaintainerRoute><Practice /></MaintainerRoute>} />
        <Route path="/interview/:sessionId/recap" element={<MaintainerRoute><Recap /></MaintainerRoute>} />

        <Route path="/offers" element={<MaintainerRoute><Offers /></MaintainerRoute>} />
        <Route path="/offers/compare" element={<MaintainerRoute><OffersCompare /></MaintainerRoute>} />
        <Route path="/negotiate/:offerId" element={<MaintainerRoute><Negotiate /></MaintainerRoute>} />

        <Route path="/outreach" element={<MaintainerRoute><Outreach /></MaintainerRoute>} />
        <Route path="/outreach/:threadId" element={<MaintainerRoute><OutreachDetail /></MaintainerRoute>} />
        <Route path="/outreach/compose" element={<MaintainerRoute><Compose /></MaintainerRoute>} />
        <Route path="/templates" element={<MaintainerRoute><Templates /></MaintainerRoute>} />

        <Route path="/cv" element={<CVPicker />} />
        <Route path="/my-cv" element={<MyCV />} />
        <Route path="/cv/editor" element={<CVEditor />} />
        <Route path="/cv/upload" element={<UploadCV />} />
        {/* 中文自述 → 英文 CV (UK-CN MVP) */}
        <Route path="/cv/new" element={<NarrativeCV />} />

        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<MaintainerRoute><Settings /></MaintainerRoute>} />
        <Route path="/settings/filter" element={<MaintainerRoute><SettingsFilter /></MaintainerRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/matches" replace />} />
    </Routes>
    </>
  );
}
